/**
 * The app sidebar: brand and its collapse control, primary nav, then the Billing group
 * and the collapsible Settings & Resources group.
 *
 * Structure, order, labels, icons, spacing, and the group heading style are
 * verbatim from the prototype. Eight things have changed and none is a redesign:
 *
 *   - Rows are router links (see NavItem) instead of buttons that set a view
 *     string, per the Phase 2 brief.
 *   - The brand is a link to Home, in that same router `<Link>`. It was inert, which
 *     is the one thing on a page every user expects to be clickable.
 *   - The lock icons still come from entitlements, so the padlocks appear and
 *     disappear exactly as before. They are presentation only: the locked routes
 *     render their own upgrade prompt, which is where the gate lives.
 *   - Two rows changed in the groups (Batch 7 §6): "Support Ticket" left Resources — the
 *     row only, not the feature; /ticket is still a route and Support Center still opens
 *     it — and "Upgrade" joined Billing, pointing at the Products & Billing page that
 *     already existed and had no navigation entry. Both are annotated where they sit.
 *   - "Resources" is now "Settings & Resources", and the Settings row MOVED into it from
 *     the foot of the primary group — the same row, the same route, one group along, so the
 *     four rows about the app's own apparatus sit under one heading. No page was added,
 *     duplicated or routed twice; the reasoning is at the group itself.
 *   - That group is now COLLAPSIBLE, shut by default, using the disclosure the Create group
 *     has had all along — the same `useState`, the same `.lyx-nav-chevron` button, the same
 *     `|| compact` escape hatch. Four rows about the app's apparatus were permanently
 *     occupying a third of the list; they are one press away instead. No route changed and
 *     no second navigation implementation was introduced.
 *   - "Premium Features" is no longer a heading of its own. DFY Producer Vault MOVED into
 *     Billing — the same <NavItem>, the same `to`, the same `locked={!dfyVault}` — because a
 *     one-row group is a heading with nothing to group, and the row is something you buy.
 *     Nothing about the entitlement, the gate or the purchase flow moved with it.
 *   - Below 900px it narrows to an icon rail on its own, from the stylesheet alone, and
 *     below 560px it is hidden entirely and reopened as an overlay drawer. The rows are
 *     the same rows either way — there is no second navigation and no duplicated nav
 *     list. What varies is the viewport width and, in drawer mode, a close button beside
 *     the brand.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * THE COLLAPSE CONTROL, and how it is not the control that was removed.
 *
 * The sidebar can be narrowed to its icons on purpose, by an ICON-ONLY button in the TOP
 * area of the sidebar, beside the brand. It is asked for as a new experience, and it is a
 * different object from the collapse toggle removed earlier in every respect that can be
 * pointed at:
 *
 *   what      `PanelLeft` — one glyph, one meaning: this is the sidebar. The old pair was
 *             `PanelLeftClose` / `PanelLeftOpen`, two glyphs swapping under one control,
 *             which is a state signal drawn twice.
 *   called    "Minimise navigation" / "Expand navigation", and the name is the whole state
 *             signal. The old one was "Collapse navigation sidebar" / "Expand navigation
 *             sidebar" plus `aria-expanded` on the button — a name and a state saying the
 *             same thing twice. There is no `aria-expanded` here, on the button or the
 *             <nav>: nothing is being disclosed, a width is being chosen.
 *   named CSS `.lyx-nav-minimise` for the button and `.lyx-sidebar-compact` for the state.
 *             The old class was `.lyx-sidebar-collapsed`, and it is still absent from the
 *             stylesheet — asserted in styles/responsive.test.ts.
 *   held      `preferencesStore.navCompact`, beside the theme, under `Lyrixa.nav`. The old
 *             flag was `uiStore.navCollapsed` under `Lyrixa.navCollapsed`, and it was
 *             toggled from two buttons — the sidebar's and a duplicate in the Studio
 *             header — which is the arrangement that made it worth removing. There is
 *             exactly one control now, and no route renders a copy of it.
 *
 * WHERE IT MOVED FROM, AND WHY THAT IS AN IMPROVEMENT RATHER THAN A REVERSAL. It used to be
 * the last ROW of the navigation, a full-width button in NavItem's clothes reading
 * "Minimise" beside a `ChevronsLeft`. Two things were wrong with that, and both are about
 * the same confusion: a row in the navigation's own idiom, sitting in the list of places,
 * reads as a place — and it was the one row whose visible text was a verb. The brief asks
 * for "a proper icon-only sidebar toggle" at the top, and the top is where every other
 * application puts the control that resizes the panel it is in: with the panel's own
 * identity, apart from the panel's contents. So the word is gone, the glyph is the whole
 * face, and the accessible name is unchanged.
 *
 * WHAT MINIMISED LOOKS LIKE. 76px, icons centred, labels gone, the logo's wordmark gone and
 * its amber mark kept — the compact brand the logo already provides for tight spaces. Every
 * row is still there, still marks the route you are on, and still names itself to a screen
 * reader and on hover. Both collapsible groups sit open as icons rather than behind their
 * chevrons, because a disclosure with no labels to disclose would put seven destinations
 * behind controls that cannot be drawn. The main column takes the freed width on its own,
 * because `.lyx-main` is `flex: 1`.
 *
 * IT SURVIVES BEING MINIMISED IN PLACE, which was the strongest argument for the old foot
 * row and is not lost by moving. The brand slot becomes a column at 76px
 * (`.lyx-sidebar-compact .lyx-sidebar-brand { flex-direction: column }`), so the button
 * drops below the mark rather than being squeezed beside it — still on screen, still the
 * only control that restores the column, and still inside the <nav> it acts on.
 *
 * NOT IN DRAWER MODE. The preference is about a column's width and there is no column
 * there — see useNavSidebar's `compact`. The brand slot holds the drawer's close button
 * instead, and the collapse button is simply not rendered.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * THE BRAND SLOT HOLDS EXACTLY ONE CONTROL, AND WHICH ONE DEPENDS ON THE MODE: the collapse
 * button in a column, the close button in a drawer. Never both — there is no column to
 * narrow inside an overlay, and nothing to close about a column. The logo itself is a link
 * to Home rather than a control: a destination, in the navigation's own `<Link>`, and not
 * the removed toggle returning under a new hat — that one was `uiStore.navCollapsed`,
 * `.lyx-sidebar-collapsed` and a duplicate in the Studio header, all three of which are
 * still gone and asserted gone. The 900px rail is untouched too — it is what the layout does
 * on its own, driven by the media queries on `.lyx-sidebar`, and the `.lyx-sidebar-compact`
 * class opts into the same declarations at any width rather than describing the rail a
 * second time.
 *
 * The `lyx-sidebar` / `lyx-nav-label` / `lyx-resources-label` classes carry the
 * responsive rail rules from the global stylesheet, `lyx-sidebar-compact` carries the
 * minimised state, and `lyx-sidebar-drawer` carries the overlay state.
 *
 * WHY NEITHER PIECE OF STATE IS LOCAL: five things act on the drawer — the Studio's
 * hamburger, the overlay, Escape, a route change and the viewport — and the minimise
 * preference has to outlive the component that sets it. See hooks/useNavSidebar.ts.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import {
  BadgeCheck,
  BookOpen,
  ChevronDown,
  CirclePlus,
  CreditCard,
  Disc3,
  Dna,
  FileText,
  Gift,
  Home,
  Image as ImageIcon,
  LifeBuoy,
  Library,
  Lock,
  LogOut,
  Mic2,
  Package,
  PanelLeft,
  Radio,
  Handshake,
  Settings,
  Shuffle,
  X,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import sidebarLogo from '../../assets/02.png';
import NavItem, { NavActionItem, NavDisclosureItem } from './NavItem';
// Direct rather than through the ui barrel: AppShell imports the barrel
// and the barrel imports Tooltip, so the shortest path is the one that already works.
import Tooltip from '../ui/Tooltip';
import { wrapTabFocus } from '../../lib/focusTrap';
import { paths } from '../../routes/paths';
import { useEntitlementsStore } from '../../stores/entitlementsStore';
import { useContentUpdatesStore } from '../../stores/contentUpdatesStore';
import { useUiStore } from '../../stores/uiStore';
import { c } from '../../styles/theme';

/** Uppercase label above a nav group. */
function GroupLabel({ children }: { children: string }) {
  return (
    <p
      className="lyx-resources-label"
      style={{
        fontSize: 10,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        // A group heading is read. At 10px uppercase it is the smallest type in the
        // nav, which is where text3's 3.09:1 hurt most. See contrast.test.ts.
        color: c.text2,
        margin: '0 0 6px',
        padding: '0 10px',
      }}
    >
      {children}
    </p>
  );
}

/**
 * The rotating triangle that states whether a nav group is open.
 *
 * ONE GLYPH, TWO DISCLOSURES — the Create group's and Settings & Resources'. Lifted out of
 * the Create group verbatim (same size, same colour, same 0.15s rotation) at the moment a
 * second group needed the same signal, which is the alternative to a second one written
 * slightly differently.
 *
 * PRESENTATION ONLY, and deliberately not the whole control: the two disclosures have
 * genuinely different shapes and forcing one component over both would be the wrong sharing.
 * Create's parent row is a LINK, so its chevron has to be a separate button beside it — one
 * control cannot both follow a link and toggle a disclosure. Settings & Resources has no
 * destination of its own, so its header IS the button and a second nested one would be
 * invalid markup as well as two controls for one job. What they share is this glyph and its
 * class; what they do not share is the element it sits in.
 *
 * NO CLASS OF ITS OWN, in either caller. Create's sits inside a `.lyx-nav-chevron` button,
 * which is what global.css hides at 76px and on the rail; the Settings & Resources one has
 * to stay visible on the rail — it is the entire face of the control there, once the
 * heading's words are hidden — and its whole button goes at 76px. So the hiding is decided
 * by the element around the glyph in both cases, and the glyph itself is never a selector.
 */
function DisclosureChevron({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={13}
      color={c.text2}
      style={{
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.15s',
      }}
    />
  );
}

function NewContentDot({ label }: { label: string }) {
  return (
    <span
      aria-label={label}
      title={label}
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: c.accent,
        boxShadow: `0 0 0 2px ${c.accentSoft}`,
      }}
    />
  );
}

export interface SidebarProps {
  /** True when the sidebar is an overlay rather than a column in the layout. */
  drawer?: boolean;
  /** True when the overlay is showing. Meaningless outside drawer mode. */
  drawerOpen?: boolean;
  /** Closes the drawer. */
  onClose?: () => void;
  /**
   * True when the column is minimised to its icons.
   *
   * Already false in drawer mode by the time it arrives — useNavSidebar resolves that, so
   * this component does not have to reason about which mode outranks which. The `drawer &&`
   * guard on the button below is belt to that braces.
   */
  compact?: boolean;
  /**
   * Minimises or restores the column. The button is not rendered without it, so a Sidebar
   * mounted outside the shell simply has no collapse control rather than a dead one.
   */
  onToggleCompact?: () => void;
}

export default function Sidebar({
  drawer = false,
  drawerOpen = false,
  onClose,
  compact = false,
  onToggleCompact,
}: SidebarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const showToast = useUiStore((s) => s.showToast);
  // The Create group starts collapsed and opens when you visit Create, which is the
  // prototype's behaviour moved up one row: it was the Music DNA group, and the two
  // methods filed under Music DNA are now the three filed under Create.
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  /*
    AND SETTINGS & RESOURCES, shut by default for the same reason the Create group is: four
    rows about the app's own apparatus were permanently taking a third of the list from the
    rows about the work.

    SESSION UI STATE, unlike the persisted minimise preference. This is not remembered across
    a reload, but it does survive a route-shell remount: clearing a new-content marker must not
    also close a disclosure the user left open.
  */
  const resourcesSectionPaths = [
    paths.settings,
    paths.support,
    paths.guide,
    paths.bonus,
    paths.ticket,
    paths.topup,
    paths.admin,
  ];
  const onResourcesSection = resourcesSectionPaths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  const resourcesGroupOpen = useUiStore((s) => s.navResourcesGroupOpen);
  const setResourcesGroupOpen = useUiStore((s) => s.setNavResourcesGroupOpen);
  const toggleResourcesGroup = useUiStore((s) => s.toggleNavResourcesGroup);
  const [homeDestination, setHomeDestination] = useState(() =>
    pathname === paths.explore ? paths.explore : paths.dashboard
  );
  const createSectionPaths = [
    paths.create,
    paths.dna,
    paths.genreMixer,
    paths.imageMusic,
    paths.voiceCloner,
  ];
  const onCreateSection = createSectionPaths.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  // One field per selector: Zustand v5 compares with Object.is, so returning an
  // object literal here would produce a new reference on every store change.
  const commercial = useEntitlementsStore((s) => s.commercial);
  const reseller = useEntitlementsStore((s) => s.reseller);
  const dfyVault = useEntitlementsStore((s) => s.dfyVault);
  // The Studio is OTO 1 and its row now carries a padlock like the other two locked
  // destinations. The refusal itself is the route gate in router.tsx — this is the
  // same presentation-only convention, and the row stays clickable so the Studio can
  // explain what the plan unlocks.
  const studio = useEntitlementsStore((s) => s.studio);
  // The Publisher plan, same convention again: padlock here, refusal at the route.
  const publisher = useEntitlementsStore((s) => s.publisher);
  // Composer+ is OTO 2, and it is what the Music DNA and Genre Mixer rows under Create
  // need — so both of them carry this one flag. Same convention a third time: the padlock
  // marks the destination, and the destination does the refusing (router.tsx).
  const composerPlus = useEntitlementsStore((s) => s.composerPlus);
  const isAdmin = useAuthStore((s) => s.user?.isAdmin === true);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const appAccess = useAuthStore((s) => s.appAccess);
  const unverifiedLocked = !isAdmin && appAccess !== 'granted';
  const bonusHasNew = useContentUpdatesStore((s) => s.bonusHasNew);
  const vaultHasNew = useContentUpdatesStore((s) => s.vaultHasNew);
  const refreshContentUpdates = useContentUpdatesStore((s) => s.refresh);
  const markContentSeen = useContentUpdatesStore((s) => s.markSeen);
  const resetContentUpdates = useContentUpdatesStore((s) => s.reset);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!userId || unverifiedLocked) {
      resetContentUpdates();
      return;
    }
    const refresh = () => void refreshContentUpdates(userId);
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [dfyVault, refreshContentUpdates, resetContentUpdates, unverifiedLocked, userId]);

  useEffect(() => {
    if (!userId) return;
    if (pathname === paths.bonus || pathname.startsWith(`${paths.bonus}/`)) {
      markContentSeen('bonus', userId);
    }
    if (pathname === paths.vault || pathname.startsWith(`${paths.vault}/`)) {
      markContentSeen('vault', userId);
    }
  }, [markContentSeen, pathname, userId]);
  const closeRef = useRef<HTMLButtonElement>(null);
  /** Whatever had focus when the drawer opened, so it can be given back. */
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (pathname === paths.explore) setHomeDestination(paths.explore);
    if (pathname === paths.dashboard) setHomeDestination(paths.dashboard);
  }, [pathname]);

  const open = drawer && drawerOpen;

  function handleLogout() {
    logout();
    showToast('Logged out');
    // The auth gate would redirect anyway, but doing it here means the current page does
    // not render one frame against a signed-out store.
    navigate(paths.login, { replace: true });
  }

  /*
    Focus moves into the drawer when it opens and back out when it closes.

    Without the first half the keyboard stays on the button that opened the menu, which
    is now underneath an overlay — so Tab walks the page behind the drawer, invisibly.
    The close button is the target rather than the first nav row because it is the way
    out, and someone who opened the menu by accident should find the exit first.

    Without the second half focus lands back on <body> when the drawer closes, and the
    next Tab restarts from the top of the document rather than from the control the
    person was using.
  */
  useEffect(() => {
    if (!open) return;
    const root = navRef.current;
    if (!root) return;
    const previous = document.activeElement;
    returnFocusRef.current = previous instanceof HTMLElement ? previous : null;
    closeRef.current?.focus();
    return () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      if (!target?.isConnected) return;
      /*
        Only when the focus is now stranded — either on <body>, or still inside the
        drawer that has just been hidden. Anything else means focus moved somewhere
        deliberate in the meantime (a nav row was chosen, so the new route owns it)
        and dragging it back to a hamburger would be taking it off the page the user
        just asked for.
      */
      const active = document.activeElement;
      const stranded = active === document.body || (active !== null && root.contains(active));
      if (stranded) target.focus();
    };
  }, [open]);

  /*
    Tab is contained inside the open drawer. It covers the page and the page is not
    interactive while it does, so a Tab that escaped would be a cursor moving through
    controls the user cannot see and cannot reach with a pointer. The wrap is manual
    because there is no platform primitive for it short of `inert` on everything else,
    and the drawer's contents are a short, known list.
  */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = navRef.current;
      if (!root) return;
      /*
        The wrap, from lib/focusTrap.ts. It was hand-rolled here with a two-selector list —
        `a[href], button:not([disabled])` — which was true of this drawer's contents when it
        was written and is the kind of local truth that stops being one: the shared helper
        also counts inputs, selects, textareas and explicit tabindexes, so a control added to
        the nav later cannot fall outside the trap.
      */
      wrapTabFocus(root, e);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  /*
    A Create-section route should reveal its own group when you arrive there.

    The parent row is a disclosure now rather than a destination, so `/create` no longer
    opens the group "on the way". Opening it from the route keeps the current child visible
    on direct entry and after sidebar navigation, without turning the disclosure into a
    permanently forced-open group — the user can still collapse it again until the next
    navigation into the section.
  */
  useEffect(() => {
    if (onCreateSection) setCreateGroupOpen(true);
  }, [onCreateSection]);

  useEffect(() => {
    if (onResourcesSection) setResourcesGroupOpen(true);
  }, [onResourcesSection, setResourcesGroupOpen]);

  const hidden = drawer && !drawerOpen;

  return (
    <nav
      ref={navRef}
      className={[
        'lyx-sidebar',
        drawer ? 'lyx-sidebar-drawer' : '',
        open ? 'lyx-sidebar-drawer-open' : '',
        /*
          The minimised column. `!drawer` as well as `compact`, so an overlay can never
          carry it — the class and the drawer rules would otherwise both apply and the
          winner would be whichever came later in the file. useNavSidebar already resolves
          this; asserting it here as well costs nothing and makes the class list readable
          on its own.
        */
        compact && !drawer ? 'lyx-sidebar-compact' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Main"
      /*
        A closed drawer is off-screen but still in the DOM, so it has to be hidden from
        assistive technology and from the tab order as well as from view — otherwise
        Tab walks into a menu nobody can see. `inert` would be the modern way to say
        this; aria-hidden plus the stylesheet's `visibility: hidden` (which drops the
        subtree from the tab order too) is the version with support today.
      */
      aria-hidden={hidden || undefined}
      style={{ borderRight: `1px solid ${c.border}` }}
    >
      <div
        className="lyx-sidebar-brand"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 22,
          padding: '0 4px',
        }}
      >
        {/* This mark is specific to the authenticated navigation. Public, authentication,
            legal and print surfaces continue to use the shared Logo component. */}
        {/*
          ─────────────────────────────────────────────────────────────────────────────────
          AND IT GOES HOME. The brand was inert — the one thing on a page that every user
          expects to be clickable, and clicking it did nothing.

          A react-router `<Link>`, which is the navigation this file already uses: NavItem
          is a `<Link>`, so this is the same mechanism as the Home row below it, not a
          second one. It always points at the top-level Home dashboard, while the Home row
          can resume Home's full Explore surface. No `onClick`, no `navigate()`, no `href` the
          browser would resolve by reloading the document — the SPA handles it, and the
          drawer closes on the route change the way it does for every other row
          (useNavSidebar, keyed on pathname).

          `aria-label`, because the accessible name would otherwise come from the wordmark
          — and the wordmark is `display: none` on the rail and in compact mode, which
          would leave a link with no name at all on exactly the two widths where it is the
          only thing in the slot. "Lyrixa AI home" contains the visible words, so the
          spoken name and the seen one still agree.
          ───────────────────────────────────────────────────────────────────────────────── */}
        <Link
          to="/dashboard"
          aria-label="Lyrixsa AI home"
          /*
            The class exists for one rule. `.lyx-sidebar-compact a` and its rail twin set
            `padding: 10px !important` on every link in here, which was written for nav
            ROWS — and this link is now one of the elements it matches, so without an
            exception the brand mark would gain a 10px halo the moment the column narrowed.
            See global.css.
          */
          className="lyx-brand-link"
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 8,
            textDecoration: 'none',
          }}
        >
          <img
            src={sidebarLogo}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="lyx-sidebar-logo"
          />
        </Link>

        {/*
          The drawer's way out, and the only control in this slot in drawer mode.

          In drawer mode the sidebar covers the page, so it has to be dismissable from
          inside itself — and this is also the button the focus trap above hands focus to
          on open. Never on screen at the same time as the collapse button below: there is
          nothing to narrow inside an overlay, so the two are mutually exclusive by
          construction rather than by CSS.
        */}
        {drawer && onClose && (
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="lyx-sidebar-toggle"
            aria-label="Close navigation menu"
          >
            <X size={16} color={c.text2} />
          </button>
        )}

        {/*
          ─── THE MINIMISE / EXPAND CONTROL ───

          ICON ONLY, AND IN THE TOP AREA. It used to be the last ROW of the navigation — a
          full-width button in NavItem's clothes, reading "Minimise" beside a `ChevronsLeft`,
          behind a hairline. The brief asks for "a proper icon-only sidebar toggle" moved to
          the top, and the reasoning that put it at the foot was about it not being a
          destination. That is still true, and this is the stronger way to say it: a control
          that resizes the panel belongs with the panel's identity, not filed at the end of
          the panel's contents where a row-shaped button reads as a place you can go. The
          visible word has gone with the row — it was the one label in the nav that was a verb.

          THE NAME IS ALL THAT CARRIES THE STATE now, and there is still no `aria-expanded`
          anywhere near it. Nothing is disclosed by pressing this — every destination is
          present either way — so a disclosure state would be a claim a screen reader could
          act on and be wrong about. "Minimise navigation" and "Expand navigation" are two
          actions with two names, which is how a button whose label changes is meant to work,
          and both are unchanged from the row it replaces: nothing a screen-reader user or an
          existing test knows this control by has moved.

          ONE GLYPH, NOT TWO. `PanelLeft` says "the sidebar" and stays put; the row it
          replaces swapped `ChevronsLeft` for `ChevronsRight`, which was a second state
          signal beside the name. With no label beside it, a glyph that also changes is a
          face the user has to learn twice. The Tooltip and the `title` say the direction in
          words — the Tooltip on top of the native one for the reason Tooltip's header gives:
          `title` never appears on keyboard focus and never on touch, and this is precisely
          the control whose purpose is least obvious from its face.

          IT SURVIVES BEING MINIMISED IN PLACE, which was the best argument for the old
          position. `.lyx-sidebar-compact .lyx-sidebar-brand` is `flex-direction: column`, so
          at 76px this drops below the mark instead of being squeezed beside it — on screen,
          inside the <nav>, and still the only control that restores the column.

          NOT RENDERED IN DRAWER MODE. There is no column to narrow, the close button above
          owns this slot, and a "minimise" inside an overlay would be a second way to make the
          navigation smaller beside a control that already makes it go away. `onToggleCompact`
          being optional covers the other absence: a Sidebar rendered outside the shell in a
          unit test gets no control rather than a dead one.
        */}
        {!drawer && onToggleCompact && (
          <Tooltip label={compact ? 'Expand navigation' : 'Minimise navigation'} placement="bottom">
            <button
              type="button"
              /*
                ITS OWN CLASS, AND NOT `.lyx-sidebar-toggle`, even though the two now share a
                slot and a box. That class belonged to the REMOVED collapse button as well as
                to the drawer's close button, and its absence from a desktop sidebar is
                asserted by name in navSidebar.test.tsx precisely so the old control cannot
                come back wearing it. The geometry is shared where sharing costs nothing —
                global.css lists both selectors on one declaration list, so there is one 28px
                square in the stylesheet and not two — and the DOM keeps them distinguishable.
              */
              className="lyx-nav-minimise"
              onClick={onToggleCompact}
              aria-label={compact ? 'Expand navigation' : 'Minimise navigation'}
              title={compact ? 'Expand navigation' : 'Minimise navigation'}
            >
              {/* The glyph is the whole of this button, so it is a meaningful icon and takes
                  text2 — never text3. */}
              <PanelLeft size={16} color={c.text2} />
            </button>
          </Tooltip>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <NavItem icon={Home} label="Home" to={homeDestination} alsoActiveOn={[paths.explore]} />

        {/*
          CREATE, AS A DISCLOSURE WITH ALL FIVE METHODS UNDER IT.

          The section parent is no longer a destination of its own. `/create` still exists and
          is still the text-to-music composer, but it now sits where the rest of Create's
          methods sit too — as the first child row, "Text Music".

          That leaves one thing for the parent row to do, which is the thing disclosure rows are
          for: open and close the group's destinations. The current section is still visible at
          a glance because the row is highlighted whenever any child route is active.
        */}
        <div>
          <NavDisclosureItem
            icon={CirclePlus}
            label="Create"
            open={createGroupOpen}
            active={onCreateSection}
            controls="create-nav-subgroup"
            onClick={() => setCreateGroupOpen((o) => !o)}
            trailing={
              <span
                className="lyx-nav-chevron"
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <DisclosureChevron open={createGroupOpen} />
              </span>
            }
          />
          {/* The indent lives in the stylesheet rather than here, because it has to drop
              to 0 wherever the labels are gone — there is nothing left to indent under —
              and one of those places is a media query the JS cannot see. */}
          {/*
            OPEN WHEN MINIMISED, WHATEVER THE DISCLOSURE SAYS.

            "Do not remove or hide important navigation functionality simply because the
            sidebar is minimised." Text Music, Music DNA, Genre Mixer, Image Music and
            Voice Cloner would otherwise sit behind a disclosure whose extra affordance is not
            drawn at 76px.

            An `||` rather than forcing `createGroupOpen` true in state: the user's own choice
            about the disclosure is untouched, so restoring the sidebar returns the group to
            whatever state they left it in rather than to open.
          */}
          {(createGroupOpen || compact) && (
            <div
              id="create-nav-subgroup"
              className="lyx-nav-subgroup"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                marginTop: 2,
              }}
            >
              {/* The brief's order, with Text Music first because `/create` is what the old
                  Create row actually opened.

                  The first row below is the existing text-to-music route, not a new screen.
                  Music DNA and Genre Mixer still carry the Composer+ padlock, Image Music still
                  carries only the verified-access lock, and Voice Cloner now gets a sidebar-only
                  lock look while remaining a normal destination semantically. */}
              <NavItem
                icon={FileText}
                label="Text Music"
                to={paths.create}
                locked={unverifiedLocked}
              />
              <NavItem
                icon={Dna}
                label="Music DNA"
                to={paths.dna}
                locked={unverifiedLocked || !composerPlus}
              />
              <NavItem
                icon={Shuffle}
                label="Genre Mixer"
                to={paths.genreMixer}
                locked={unverifiedLocked || !composerPlus}
              />
              <NavItem
                icon={ImageIcon}
                label="Image Music"
                to={paths.imageMusic}
                locked={unverifiedLocked}
              />
              <NavItem
                icon={Mic2}
                label="Voice Cloner"
                to={paths.voiceCloner}
                visualLocked
                trailing={<Lock size={11} color={c.text2} />}
              />
            </div>
          )}
        </div>

        {/*
          Two rows, because they are two places.

          The Library is where your tracks and playlists live; the Studio is the
          workspace you do work in. One row used to serve both — "Lyrixa Studio"
          pointed at /library — so choosing the Studio showed a list and reaching the
          workspace took another click, and there was no row at all for the Library.

          `exact` on the Library: /library/:songId IS the Studio workspace, so without
          it both rows would highlight while a track is open. It claims /playlists
          instead, which is the Library's other view and reached from its tab strip.
          The Studio row claims '/library/' — the workspace it resolves to.
        */}
        <NavItem
          icon={Library}
          label="Library"
          to="/library"
          exact
          alsoActiveOn={['/playlists']}
          locked={unverifiedLocked}
        />
        <NavItem
          icon={Disc3}
          label="Lyrixsa Studio"
          to="/studio"
          alsoActiveOn={['/library/']}
          locked={unverifiedLocked || !studio}
        />
        {/*
          THE PUBLISHER, AS A TOP-LEVEL ROW.

          Not under Create, not under the Studio, and not inside the Library — §4 rules
          all of those out by name, and it is right to: the Publisher is what you do
          with a finished song rather than another way to make or edit one. Its own
          product, its own plan, its own place in the nav.

          It sits after the Studio because that is the order of the work — create,
          collect, produce, publish — and before Settings, which is not part of that
          sequence.

          `alsoActiveOn` is unnecessary here: every Publisher URL is under /publisher/,
          which the default prefix rule already claims, so the row stays lit through the
          workflow and the release pages.
        */}
        <NavItem
          icon={Radio}
          label="Publisher"
          to="/publisher"
          locked={unverifiedLocked || !publisher}
        />
        {/*
          SETTINGS USED TO BE THE LAST ROW OF THIS GROUP, and it has MOVED rather than been
          removed — it is now the first row of "Settings & Resources" at the foot of the nav.
          Same <NavItem>, same `to`, same `alsoActiveOn`, same route: one destination, moved.

          Why it left: this group is the work — create, collect, produce, publish — and the
          comment on the Publisher row above already said Settings "is not part of that
          sequence". It sat here because it had nowhere else to be. The Resources group is
          where the app's own apparatus lives (help, guide, extras), which is what Settings
          is, so grouping them puts one heading over four rows that belong together instead of
          one orphan above three.
        */}
      </div>

      <div style={{ flex: 1 }} />

      <GroupLabel>Billing</GroupLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
        {/*
          ---- UPGRADE ---- (Batch 7 §6)

          THE PLANS PAGE WAS NOT MISSING, IT WAS UNREACHABLE FROM THE NAVIGATION, and that is
          the whole finding. routes/Products.tsx has existed all along and is routed twice — at
          `/products` and at `/upgrade` (see router.tsx) — but the only ways in were Settings'
          "Products & Billing" button and the locked-feature prompts. So a user who had not
          bought anything and had no reason to open Settings could not find the page that sells
          the product. Nothing was hidden deliberately; a row was never added.

          SO THIS IS A LINK, NOT A PAGE. No second billing surface, no duplicate plan
          definitions, no plan objects in this component — it points at the page Settings and
          every upgrade prompt already point at, and `data/products.ts` remains the one place
          the catalogue is described.

          `paths.upgrade` RATHER THAN `paths.products`, matching what the rest of the app links
          to: both routes render the same page, and "upgrade" is the URL Settings' button and
          the three locked states use. One destination reached by one name, so the row lights up
          on the same URL those buttons produce.

          IN BILLING, ABOVE COMMERCIAL LICENSE. This group is what you pay for; the page is
          where all of it is bought, so it reads first and the specific licence follows. It
          carries no padlock, deliberately — there is nothing to unlock about the page that
          sells the unlocks, and a locked row here would be a closed door in front of the till.

          `CreditCard` AND NOT `CirclePlus`, WHICH IS THE CREATE ROW'S GLYPH. On the 900px rail
          and in the minimised column the labels are gone, so the icon IS the row — two rows
          sharing one glyph there would be two unlabelled buttons a user has to guess between.
          The card also says what the destination is rather than how it feels: this goes to
          Products & Billing.
        */}
        <NavItem icon={CreditCard} label="Upgrade" to="/upgrade" alsoActiveOn={['/products']} />
        <NavItem
          icon={BadgeCheck}
          label="Commercial License"
          to="/licenses"
          alsoActiveOn={['/license/verify', '/agreement']}
          locked={unverifiedLocked || !commercial}
        />
        {reseller ? <NavItem icon={Handshake} label="Reseller" to={paths.reseller} /> : null}
        {/*
          ---- DFY PRODUCER VAULT ----

          IN BILLING NOW, AND THE "Premium Features" HEADING HAS GONE WITH IT. The row is the
          same <NavItem> it always was: same icon, same label, same `to`, same
          `locked={!dfyVault}`. Nothing about the entitlement, the route gate, the upgrade
          prompt or the purchase flow is touched by this — the Vault refuses at the route, as
          every gated destination in this file does, and this is presentation only.

          WHY THE HEADING WENT. "Premium Features" was a group of one, which is a heading with
          nothing to group — and at 76px and on the 900px rail the headings are hidden
          entirely, so it was a divider with no label above a single icon. Two group labels
          for three rows is also more furniture than the list can carry.

          AND WHY BILLING IS WHERE IT LANDS rather than beside the other locked rows. Billing
          is what you pay for, and the Vault is a purchase — the same kind of thing as the
          Commercial License directly above it, which is also padlocked until it is bought.
          The rows about the WORK stay in the primary group; the rows about what you own are
          these three.

          LAST IN THE GROUP because the order is the money: the page that sells everything,
          then the licence, then the specific pack.
        */}
        <NavItem
          icon={Package}
          label="DFY Producer Vault"
          to="/vault"
          locked={unverifiedLocked || !dfyVault}
          trailing={
            dfyVault && vaultHasNew ? (
              <NewContentDot label="New Vault content available" />
            ) : undefined
          }
          onClick={() => {
            if (userId) markContentSeen('vault', userId);
          }}
        />
      </div>

      {/*
        "SETTINGS & RESOURCES" — ONE COLLAPSIBLE GROUP, shut by default.

        WHAT CHANGED, AND WHAT DID NOT. The four rows are the four rows: Settings is the same
        <NavItem> that was once the last row of the primary group, and the three below it have
        not moved since the prototype. `paths` is untouched, router.tsx is untouched, and there
        is still exactly one /settings, /support, /guide and /bonus route with exactly one row
        each. What changed is that the four are behind a disclosure instead of permanently on
        screen — four rows about the app's own apparatus were taking a third of the list from
        the rows about the work.

        THE HEADING IS THE BUTTON, which is the shape this group needs and the Create group
        cannot use. Create has a destination of its own, so its row is a link and the chevron
        has to be a separate button beside it. This group has no destination — "Settings &
        Resources" was never clickable — so the heading itself becomes the control, and there
        is one thing to press rather than a label with a triangle hidden at the end of it.
        `aria-expanded` states the disclosure, and unlike the minimise control that is
        correct here: something genuinely is being shown and hidden.

        WHY SETTINGS READS FIRST. It is the row of the four a user reaches for most, and the
        heading names it first. Support Center, the Guide and the Bonus follow in the order
        they already had.

        THE NAME COVERS BOTH HALVES. "Resources" alone did not describe a group containing the
        app's settings, and leaving the heading unchanged while adding Settings under it would
        have been a heading that lies about its own contents.

        `.lyx-resources-label` STAYS ON THE VISIBLE TEXT, not on the button. That class is
        `display: none` at 76px and on the 900px rail, and the button is the only route to the
        four rows there — hiding the control instead of its wording would leave them
        unreachable. The wording goes, the button stays, and its accessible name still says
        what it does.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button
          type="button"
          className="lyx-nav-group-toggle"
          onClick={toggleResourcesGroup}
          aria-expanded={resourcesGroupOpen}
          aria-label={
            resourcesGroupOpen
              ? 'Collapse Settings & Resources menu'
              : 'Expand Settings & Resources menu'
          }
          style={{
            // The GroupLabel geometry it replaces, so the heading has not moved or changed
            // size — it has become pressable. `width: 100%` and the transparent background
            // are what a <button> needs to sit in a column like the <p> did.
            //
            // The 4px of vertical padding and the radius are the two additions, and they
            // are what the hover wash needs to land on: `.lyx-nav-group-toggle:hover`
            // painting a 13px-tall strip with square corners would read as a rendering
            // fault rather than as a control. 4px + 4px against the <p>'s 6px bottom
            // margin, so the group heading sits within a pixel of where it always did.
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            padding: '4px 10px',
            margin: '0 0 2px',
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span
            className="lyx-resources-label"
            style={{
              fontSize: 10,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              // Same reasoning as GroupLabel's: a group heading is read, and at 10px
              // uppercase it is the smallest type in the nav. See contrast.test.ts.
              color: c.text2,
              flex: 1,
            }}
          >
            Settings &amp; Resources
          </span>
          {/* NOT GIVEN `.lyx-nav-chevron`, unlike the Create group's, and the difference is
              deliberate. That class is what the compact column and the 900px rail hide, and
              on the rail this triangle is the ONLY face this control has left: the heading's
              wording is hidden there, `compact` is false, so the group is genuinely closed
              and these four destinations are reachable through nothing else. In the compact
              column the question does not arise — the whole button is hidden there and the
              group is forced open instead (see the render condition below). */}
          <DisclosureChevron open={resourcesGroupOpen} />
        </button>
        {/*
          OPEN WHEN MINIMISED, WHATEVER THE DISCLOSURE SAYS — the same `||` the Create group
          uses, for the same reason and with the same consequence.

          At 76px and on the 900px rail the heading's words and the chevron are both hidden by
          global.css. The button survives, so the disclosure is still operable, but its face
          is a bare triangle above four hidden rows — so the rows are shown instead, as icons,
          exactly as the Create group's three are. "Do not remove or hide important navigation
          functionality simply because the sidebar is minimised": Settings is not something to
          put behind a control that cannot be read.

          An `||` rather than forcing the flag true, so restoring the column returns the group
          to whatever state the user left it in rather than to open.
        */}
        {(resourcesGroupOpen || compact) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/*
              `/topup` IS STILL CLAIMED HERE, and `/upgrade` still is not (Batch 7 §6).

              Both carried over with the row when it moved, because the reasoning is about
              which URLs have a row of their own and not about which group the row is in:
              buying credits still has no row and is still reached from Settings, so this one
              lights up for it, while `/upgrade` has its own row in Billing and claiming it
              twice would mark two rows `aria-current="page"` on one URL.
            */}
            <NavItem icon={Settings} label="Settings" to="/settings" alsoActiveOn={['/topup']} />
            {isAdmin && (
              <NavItem
                icon={LifeBuoy}
                label="Admin"
                to="/admin"
                alsoActiveOn={[
                  '/admin',
                  '/admin/support',
                  '/admin/users',
                  '/admin/bonuses',
                  '/admin/vault',
                ]}
              />
            )}
            {/*
              SUPPORT TICKET IS NOT A ROW HERE ANY MORE (Batch 7 §6), AND THE FEATURE IS
              UNTOUCHED.

              What was removed is one duplicate ENTRY POINT, not the capability: /ticket is
              still a route, routes/Ticket.tsx is unchanged, and Support Center — the row
              directly below — opens it with a button of its own. Raising a ticket is something
              you do after reading the help, so it belongs on the support page rather than
              beside it; two sibling rows made the nav offer a choice between a place and an
              action taken in it.

              A test asserts /ticket is still reachable from Support Center, because "removed
              from the nav" and "removed from the app" are one careless edit apart.
            */}
            <NavItem icon={LifeBuoy} label="Support Center" to="/support" />
            {/*
              STILL "Lyrixa AI Guide", which is what routes/Guide.tsx puts in its own <h1>. The
              brief's list of this group's contents says "Lyrixa AI Guide" now, which is this
              label; the earlier brief said "Lyrixa Guide" and shortening the row would have
              left the nav naming one thing and the page it opens naming another.
            */}
            <NavItem icon={BookOpen} label="Lyrixsa AI Guide" to="/guide" />
            <NavItem
              icon={Gift}
              label="Lyrixsa Bonus"
              to="/bonus"
              locked={unverifiedLocked}
              trailing={bonusHasNew ? <NewContentDot label="New bonus available" /> : undefined}
              onClick={() => {
                if (userId) markContentSeen('bonus', userId);
              }}
            />
          </div>
        )}
      </div>

      {/*
        LOG OUT IS THE LAST NAV ACTION NOW, and deliberately not folded into Settings &
        Resources above it. The user's request is for the last thing in the navbar, and a row
        inside a shut disclosure would be neither last nor always visible. It is an action, not
        a destination, so it uses the nav-action button rather than pretending to be a link.
      */}
      <div style={{ marginTop: 8 }}>
        <NavActionItem icon={LogOut} label="Log out" onClick={handleLogout} />
      </div>
    </nav>
  );
}
